// Apolo IDE — KillSwitchButton (Sprint v4 Epic S3 WS S3.3 + S3.4).
//
// Standalone button + global ⇧⌘K shortcut that cancels the currently active
// dispatch. Calls `onKill` (provided by parent — typically `useOrchestrator
// .kill`) and degrades gracefully when no dispatch is active.
//
// Like useSessionShortcuts, the keydown handler skips when the user is
// typing in an input/textarea/contenteditable so chat composers are not
// hijacked. Only fires when `enabled` AND there's an active dispatch.

import { useEffect, useState } from 'react';
import { Ban, Loader2 } from 'lucide-react';

function isTypingTarget(target) {
  if (!target) return false;
  if (target.isContentEditable) return true;
  const tag = target.tagName;
  return tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT';
}

export default function KillSwitchButton({
  onKill,
  enabled = true,
  busy = false,
  label = 'Cancelar',
  reason = 'manual_shortcut',
  showShortcut = true,
  className,
}) {
  const [firing, setFiring] = useState(false);
  const disabled = !enabled || busy || firing;

  // ⇧⌘K global shortcut. Skipped when typing.
  useEffect(() => {
    if (!enabled || !onKill) return undefined;
    function handler(e) {
      if (isTypingTarget(e.target)) return;
      const meta = e.metaKey || e.ctrlKey;
      const shift = e.shiftKey;
      if (!meta || !shift) return;
      if (e.key === 'k' || e.key === 'K') {
        e.preventDefault();
        if (disabled) return;
        setFiring(true);
        Promise.resolve(onKill(reason)).finally(() => setFiring(false));
      }
    }
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [enabled, onKill, disabled, reason]);

  function handleClick() {
    if (disabled || !onKill) return;
    setFiring(true);
    Promise.resolve(onKill(reason)).finally(() => setFiring(false));
  }

  return (
    <button
      type="button"
      onClick={handleClick}
      disabled={disabled}
      data-testid="kill-switch-button"
      aria-label="Cancelar dispatch (Shift+Cmd+K)"
      title="⇧⌘K"
      className={
        className ||
        'inline-flex items-center gap-1 rounded-md border border-apolo-borderSubtle bg-apolo-surface1 px-2 py-1 text-[11px] text-apolo-error transition hover:bg-apolo-error/10 disabled:cursor-not-allowed disabled:opacity-50'
      }
    >
      {firing ? (
        <Loader2 size={11} className="animate-spin" aria-hidden="true" />
      ) : (
        <Ban size={11} aria-hidden="true" />
      )}
      {label}
      {showShortcut ? (
        <kbd className="ml-1 rounded border border-apolo-borderSubtle bg-apolo-surface2 px-1 font-mono text-[9px] text-apolo-textTertiary">
          ⇧⌘K
        </kbd>
      ) : null}
    </button>
  );
}
